import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from 'react';

const MS_PER_SECOND = 1000;
/** A seek that never fires `seeked` (e.g. the browser clamped it) resolves after this. */
const SEEK_TIMEOUT_MS = 2000;
/**
 * How long a frame wait gives a paused video to paint. A timer, not animation
 * frames: those never fire in a background tab, which would hang a capture
 * started just before a tab switch; a timer is only throttled there.
 */
const PAINT_FALLBACK_MS = 50;
/** readyState HAVE_CURRENT_DATA — the first frame can be drawn. */
export const READY_STATE_HAVE_CURRENT_DATA = 2;

export interface Intrinsic {
  width: number;
  height: number;
}

export interface SeekOptions {
  pause?: boolean;
}

/**
 * The one interface the rail and composer use to talk to the player, so
 * nothing else touches the `<video>` element directly.
 */
export interface MediaController {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** The mounted element (null before mount); changes when the theater re-keys the <video>. */
  element: HTMLVideoElement | null;
  /**
   * The playhead, read live. It moves on every `timeupdate` without being
   * React state, so the controller keeps one identity during playback and
   * nothing re-renders for it. A component that shows the time reads it
   * through `useMediaTime` instead.
   */
  readonly currentTimeMs: number;
  durationMs: number;
  paused: boolean;
  /** Metadata loaded and the first frame is decodable. */
  ready: boolean;
  intrinsic: Intrinsic | null;
  /**
   * Calls `onChange` whenever `currentTimeMs` moves; returns the unsubscribe.
   * Optional so a stand-in whose playhead never moves needs no store.
   */
  subscribeTime?: (onChange: () => void) => () => void;
  play(): void;
  pause(): void;
  /** Resolves once the browser reports `seeked` (or after a short timeout). */
  seek(ms: number, opts?: SeekOptions): Promise<void>;
  /** Resolves once a frame has actually been presented after a pause/seek. */
  waitForPresentedFrame(): Promise<void>;
}

type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: () => void) => number;
};

/** The playhead as a tiny external store: one mutable value and whoever displays it. */
interface TimeStore {
  read(): number;
  write(ms: number): void;
  subscribe(onChange: () => void): () => void;
}

function createTimeStore(): TimeStore {
  let current = 0;
  const listeners = new Set<() => void>();
  return {
    read: () => current,
    write: (ms) => {
      if (ms === current) return;
      current = ms;
      listeners.forEach((listener) => listener());
    },
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
  };
}

function unsubscribeNothing(): void {
  // A stand-in controller's playhead never moves, so there is nothing to stop.
}

function readIntrinsic(video: HTMLVideoElement): Intrinsic | null {
  if (!video.videoWidth || !video.videoHeight) return null;
  return { width: video.videoWidth, height: video.videoHeight };
}

function isReady(video: HTMLVideoElement): boolean {
  return video.readyState >= READY_STATE_HAVE_CURRENT_DATA && Number.isFinite(video.duration) && video.duration > 0;
}

/**
 * A ref whose assignment is observable: React writes `.current` when the
 * element mounts, and again when it is replaced (the theater re-keys the
 * `<video>` on a CORS-mode flip), so the listener effect must re-run then.
 */
function useObservableRef<T>(): [RefObject<T | null>, T | null] {
  const [element, setElement] = useState<T | null>(null);
  const ref = useMemo(() => {
    let current: T | null = null;
    return {
      get current() {
        return current;
      },
      set current(next: T | null) {
        current = next;
        setElement(next);
      },
    } as RefObject<T | null>;
  }, []);
  return [ref, element];
}

/**
 * The playhead for a component that displays it: re-renders as it moves,
 * where `controller.currentTimeMs` is a silent read for event handlers.
 */
export function useMediaTime(controller: Pick<MediaController, 'currentTimeMs' | 'subscribeTime'>): number {
  const { subscribeTime } = controller;
  const subscribe = useCallback((onChange: () => void) => subscribeTime?.(onChange) ?? unsubscribeNothing, [subscribeTime]);
  const read = useCallback(() => controller.currentTimeMs, [controller]);
  return useSyncExternalStore(subscribe, read, read);
}

export function useMediaController(): MediaController {
  const [videoRef, element] = useObservableRef<HTMLVideoElement>();
  const [time] = useState(createTimeStore);
  const [durationMs, setDurationMs] = useState(0);
  const [paused, setPaused] = useState(true);
  const [ready, setReady] = useState(false);
  const [intrinsic, setIntrinsic] = useState<Intrinsic | null>(null);

  useEffect(() => {
    const video = element;
    if (!video) return undefined;

    const syncMeta = () => {
      setDurationMs(Number.isFinite(video.duration) ? video.duration * MS_PER_SECOND : 0);
      setIntrinsic(readIntrinsic(video));
      setReady(isReady(video));
    };
    const syncTime = () => time.write(video.currentTime * MS_PER_SECOND);
    const syncPaused = () => setPaused(video.paused);
    // The media was detached (a src swap): nothing is decodable until it reloads.
    const onEmptied = () => setReady(false);

    video.addEventListener('loadedmetadata', syncMeta);
    video.addEventListener('loadeddata', syncMeta);
    video.addEventListener('durationchange', syncMeta);
    video.addEventListener('timeupdate', syncTime);
    video.addEventListener('seeked', syncTime);
    video.addEventListener('play', syncPaused);
    video.addEventListener('pause', syncPaused);
    video.addEventListener('ended', syncPaused);
    video.addEventListener('emptied', onEmptied);

    syncMeta();
    syncTime();
    syncPaused();

    return () => {
      video.removeEventListener('loadedmetadata', syncMeta);
      video.removeEventListener('loadeddata', syncMeta);
      video.removeEventListener('durationchange', syncMeta);
      video.removeEventListener('timeupdate', syncTime);
      video.removeEventListener('seeked', syncTime);
      video.removeEventListener('play', syncPaused);
      video.removeEventListener('pause', syncPaused);
      video.removeEventListener('ended', syncPaused);
      video.removeEventListener('emptied', onEmptied);
    };
  }, [element, time]);

  const play = useCallback(() => {
    void videoRef.current?.play().catch(() => undefined);
  }, [videoRef]);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, [videoRef]);

  // Settles the in-flight seek (if any): removes its `seeked` listener, clears
  // its timeout, and resolves its promise, so a new seek never leaves the old
  // one's listener attached or its caller hanging.
  const pendingSeekCleanupRef = useRef<(() => void) | null>(null);

  const seek = useCallback(
    (ms: number, opts: SeekOptions = {}) => {
      const video = videoRef.current;
      if (!video) return Promise.resolve();
      pendingSeekCleanupRef.current?.();
      if (opts.pause) video.pause();
      const target = Math.max(0, ms) / MS_PER_SECOND;
      return new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          video.removeEventListener('seeked', finish);
          clearTimeout(timeoutId);
          pendingSeekCleanupRef.current = null;
          time.write(video.currentTime * MS_PER_SECOND);
          resolve();
        };
        const timeoutId = setTimeout(finish, SEEK_TIMEOUT_MS);
        video.addEventListener('seeked', finish);
        pendingSeekCleanupRef.current = finish;
        video.currentTime = target;
      });
    },
    [videoRef, time],
  );

  const waitForPresentedFrame = useCallback(() => {
    const video = videoRef.current as VideoWithFrameCallback | null;
    if (!video) return Promise.resolve();
    // requestVideoFrameCallback only fires when a *new* frame is composited,
    // so on an already-paused video it never fires. Race it against a short
    // timer, which is enough for the paused frame to paint.
    const painted = new Promise<void>((resolve) => {
      setTimeout(resolve, PAINT_FALLBACK_MS);
    });
    if (typeof video.requestVideoFrameCallback !== 'function') return painted;
    const presented = new Promise<void>((resolve) => video.requestVideoFrameCallback?.(() => resolve()));
    return Promise.race([presented, painted]);
  }, [videoRef]);

  return useMemo<MediaController>(
    () => ({
      videoRef,
      element,
      get currentTimeMs() {
        return time.read();
      },
      durationMs,
      paused,
      ready,
      intrinsic,
      subscribeTime: time.subscribe,
      play,
      pause,
      seek,
      waitForPresentedFrame,
    }),
    [videoRef, element, time, durationMs, paused, ready, intrinsic, play, pause, seek, waitForPresentedFrame],
  );
}
